import { useCallback, type MouseEvent, type ReactNode } from "react";

import { trackDappUiEvent, type DappUiFeature } from "./analytics";

export interface TrackingBoundaryProps {
  feature: DappUiFeature;
  children: ReactNode;
}

type TrackedNativeEvent = Event & { __dappUiTracked?: boolean };

/**
 * Click-delegation root embedded in each feature. Any descendant with a
 * `data-track="<action>"` attribute (optionally `data-track-id` for dynamic
 * list items) is reported as `ui: <feature>_<action>` — no per-button handler
 * code. Uses the bubble phase so that when boundaries nest (SendFlow inside
 * WalletInfo), the innermost feature claims the event first and the outer
 * boundary skips it via the native-event flag. Portal clicks still arrive
 * because React propagates synthetic events through the component tree.
 */
export function TrackingBoundary({ feature, children }: TrackingBoundaryProps) {
  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const nativeEvent = event.nativeEvent as TrackedNativeEvent;
      if (nativeEvent.__dappUiTracked) return;

      const target = event.target as Element | null;
      const tracked = target?.closest?.("[data-track]");
      if (!tracked) return;
      const action = tracked.getAttribute("data-track");
      if (!action) return;

      nativeEvent.__dappUiTracked = true;
      const itemId = tracked.getAttribute("data-track-id");
      trackDappUiEvent(feature, action, itemId ? { item_id: itemId } : undefined);
    },
    [feature],
  );

  return (
    <div style={{ display: "contents" }} onClick={handleClick}>
      {children}
    </div>
  );
}
